import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

/**
 * Laravel Echo client (Reverb / Pusher protocol) shared by both SPAs. The
 * homepage subscribes to the global `events` channel (activity feed + Live Now
 * grid) and the game screen to a per-game `game.{id}` channel; `GET /api/live/*`
 * seeds initial state and is the fallback when the socket drops.
 *
 * Config comes from the app's build env (`configureEcho({...})`, mirroring
 * {@link ./client.configureApi}) so the package stays decoupled from Vite.
 */

export interface EchoEnv {
  key?: string | undefined;
  host?: string | undefined;
  port?: string | number | undefined;
  scheme?: string | undefined;
}

export interface EchoConfig {
  broadcaster: 'reverb';
  key: string;
  wsHost: string;
  wsPort: number;
  wssPort: number;
  forceTLS: boolean;
  enabledTransports: Array<'ws' | 'wss'>;
  disableStats: true;
}

/** A value that is unset, empty, or an unsubstituted `__VITE_*__` placeholder. */
function isBlank(value: string | number | undefined): boolean {
  if (value === undefined) return true;
  const text = String(value).trim();
  return text.length === 0 || /^__VITE_\w+__$/.test(text);
}

/**
 * Builds the Reverb Echo config from build env, or returns `null` when the app
 * key is unset - realtime is optional (every live screen degrades to
 * `/api/live/*` polling), so a missing key must never throw at import time.
 */
export function resolveEchoConfig(env: EchoEnv): EchoConfig | null {
  if (isBlank(env.key)) return null;

  const scheme = isBlank(env.scheme) ? 'https' : String(env.scheme).trim().toLowerCase();
  const forceTLS = scheme === 'https';
  const host =
    isBlank(env.host) || typeof window === 'undefined'
      ? String(env.host ?? '').trim()
      : String(env.host).trim();
  const parsedPort = Number(env.port);
  const port = Number.isFinite(parsedPort) && parsedPort > 0 ? parsedPort : forceTLS ? 443 : 80;

  return {
    broadcaster: 'reverb',
    key: String(env.key).trim(),
    wsHost: host.length > 0 ? host : typeof window !== 'undefined' ? window.location.hostname : '',
    wsPort: port,
    wssPort: port,
    forceTLS,
    enabledTransports: ['ws', 'wss'],
    disableStats: true,
  };
}

/** The per-game broadcast channel name (`game.{id}`). */
export function gameChannelName(gameId: number | string): string {
  return `game.${gameId}`;
}

/** Global activity-feed / Live-Now channel name. */
export const EVENTS_CHANNEL = 'events';

/** Homepage Discord chat mirror channel name. */
export const DISCORD_CHANNEL = 'discord';

/** Broadcast payload of the `game.event` event (see backend GameEventBroadcast). */
export interface GameEventPayload {
  id: number;
  type: string;
  game_id: number | null;
  server_id: number | null;
  player_id: number | null;
  data: Record<string, unknown> | null;
  created_at: string | null;
}

/** Broadcast payload of the `game.snapshot` event (see backend GameSnapshotUpdated). */
export interface GameSnapshotPayload {
  game_id: number;
  snapshot: Record<string, unknown>;
}

/** Broadcast payload of the `discord.chat` event (see backend DiscordChatUpdated). */
export interface DiscordChatPayload {
  latest_id: string | null;
}

export const BROADCAST_EVENTS = {
  gameEvent: 'game.event',
  gameSnapshot: 'game.snapshot',
  discordChat: 'discord.chat',
} as const;

type EchoInstance = Echo<'reverb'>;

let configuredEnv: EchoEnv = {};
let echoSingleton: EchoInstance | null = null;

/** Configure the Echo client once at app startup, before any subscription. */
export function configureEcho(env: EchoEnv): void {
  configuredEnv = env;
}

/**
 * Lazily constructs the shared Echo instance, or returns `null` when realtime
 * is not configured (missing key) or unavailable (no `window`). Callers treat
 * `null` as "degrade to polling".
 */
export function getEcho(): EchoInstance | null {
  if (echoSingleton !== null) return echoSingleton;
  if (typeof window === 'undefined') return null;

  const config = resolveEchoConfig(configuredEnv);
  if (config === null) return null;

  /* c8 ignore start - thin wiring over laravel-echo/pusher-js; not unit-tested */
  const globalWithPusher = window as unknown as { Pusher?: typeof Pusher };
  globalWithPusher.Pusher = Pusher;
  echoSingleton = new Echo(config);
  return echoSingleton;
  /* c8 ignore stop */
}

/** Tear down the socket (used on logout / full app teardown). */
export function disconnectEcho(): void {
  /* c8 ignore start - depends on a live Echo instance */
  if (echoSingleton !== null) {
    echoSingleton.disconnect();
    echoSingleton = null;
  }
  /* c8 ignore stop */
}
